import {
  Box,
  capitalize,
  Grid2,
  Typography,
  useMediaQuery
} from '@mui/material'
import { withObservables } from '@nozbe/watermelondb/react'
import { CalendarBackIcon, CaretDownIcon, GroupChannelIcon } from 'assets/icons'
import ContainerWithBottomBadgeIcon from 'components/containers/ContainerWithBottomBadgeIcon'
import GroupPopup from 'components/popups/groupInfo/GroupPopup'
import GroupTitle from 'components/popups/groupInfo/GroupTitle'
import { useCalendar } from 'components/providers/CalendarProvider'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import ObserveService from 'services/db/Observe'
import { isUserLeft } from 'services/db/utils/GroupUtils'
import LocalStorage from 'services/LocalStorage'
import { AccountDoc, GroupDoc, UserSettingsDoc } from 'types/api/changes'
import { CalendarRange } from 'types/components/calendar'
import { Option } from 'types/components/form'
import { Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import { highestActiveTier } from 'utils/api/accounts'
import {
  defaultGroupImage,
  getActiveParticipants,
  groupsToImages,
  isGroupChannel,
  isGroupOutOfTier,
  isUserActiveInGroup
} from 'utils/api/groups'
import NamesStrip from './NamesStrip'
import * as Styled from './styled'
import { t } from 'i18next'
import GroupcalTooltip from 'components/tooltips/GroupcalTooltip'
import i18n from 'i18n'
import { SEARCH_MODE } from 'components/popups/searchItems/SearchPopup'
import SearchPopupButton from 'components/SearchPopupButton'
import { is24meApp } from 'utils/appType'
import { isGuest } from 'utils/user'
import { isMobileDevice } from 'utils/constants'
import { theme } from 'theme'
import Avatar from 'components/badges/Avatar'
import ViewDatesPicker from './ViewDatesPicker'
import { DateRange } from 'react-day-picker'
import { isTrulyEmpty } from 'utils/object'
import {
  ONBOARDING_MODE,
  useTfmTourProvider
} from 'components/onboarding/TFMTourProvider'
import { useTour } from '@reactour/tour'
import Logger from 'services/Logger'

export const RANGE: Option[] = [
  { label: capitalize(t('day_one')), value: CalendarRange.DAY },
  { label: capitalize(t('week_one')), value: CalendarRange.WEEK },
  { label: capitalize(t('month_one')), value: CalendarRange.MONTH },
  { label: capitalize(t('list')), value: CalendarRange.LISTWEEK },
  { label: capitalize(t('dayList')), value: CalendarRange.LISTDAY }
]

export const dateFormat = 'YYYY-MM-DD'

interface CalendarHeaderProps {
  account?: Model<AccountDoc>
  groups?: Model<GroupDoc>[]
  userSettings?: Model<UserSettingsDoc>
  currentGroup?: Model<GroupDoc>
  groupID: string
  currentView?: CalendarRange
  title?: string
  embedMode?: boolean
  dateRangeSelected?: (selected: Date | DateRange | undefined) => void
}

function CalendarHeader(props: CalendarHeaderProps) {
  const calendar = useCalendar()

  const isHeightBelowCondesed = useMediaQuery('(max-height: 480px)')

  const tour = useTour()
  const tfmTour = useTfmTourProvider()

  return (
    <Styled.CalendarHeader
      style={{
        cursor: isTrulyEmpty(calendar.groupServerID) ? 'default' : 'pointer'
      }}
      className="calendar-header"
      display={'flex'}
      flexDirection={isMobileDevice() ? 'column' : 'row'}
    >
      {props.currentGroup?._id ? (
        <Styled.CalendarHeaderInfo
          id="calendar-info-calendar"
          onClick={() => {
            calendar.onGroupInfo(props.currentGroup?._id)
            setTimeout(() => {
              if (
                tour.setSteps &&
                tfmTour.getStepsByMode &&
                !tfmTour.isPlaying
              ) {
                const steps = tfmTour.getStepsByMode(
                  ONBOARDING_MODE.FIRST_LOGIN,
                  () => {
                    const clearSteps = tfmTour.getStepsByMode!(
                      ONBOARDING_MODE.FIRST_LOGIN
                    )

                    return (
                      tfmTour.isStepCompleted?.('#group-details-popup') ===
                        false &&
                      clearSteps.length > 0 &&
                      clearSteps[0].selector === '#group-details-popup'
                    )
                  }
                )

                if (steps.length === 0) return

                LocalStorage.set(LocalStorageKey.SHOULD_KEEP_POPUP, '1')
                tfmTour.setMode(ONBOARDING_MODE.FIRST_LOGIN)
                tour.setSteps(steps)
                tour.setCurrentStep(0)
                tour.setIsOpen(steps.length > 0)
              }
            }, 250)
          }}
        >
          <GroupPhotoElement {...props} />

          <GroupTitleElement {...props} />
        </Styled.CalendarHeaderInfo>
      ) : (
        <Styled.CalendarHeaderInfo>
          <Styled.AllAccountBadge
            groups={props.groups}
            userSettings={props.userSettings}
            images={groupsToImages(props.groups)}
          />
          <Styled.CalendarHeaderTitleContainer>
            <Styled.CalendarHeaderTitle
              sx={{
                fontSize: isHeightBelowCondesed ? '16px' : '1.5rem',
                marginRight: i18n.dir() === 'ltr' ? 0 : 8
              }}
            >
              {t('allCalendarsButton')}
            </Styled.CalendarHeaderTitle>
          </Styled.CalendarHeaderTitleContainer>
        </Styled.CalendarHeaderInfo>
      )}

      <Styled.CalendarHeaderActions
        justifyContent={isMobileDevice() ? 'space-between' : undefined}
        alignItems={isMobileDevice() ? undefined : 'center'}
        width={isMobileDevice() ? '100%' : 'fit-content'}
      >
        <NextPrevButtons dateRangeSelected={props.dateRangeSelected} />
        <NavButtons {...props} />

        {isGuest() || props.embedMode ? (
          <></>
        ) : (
          <SearchPopupButton
            groupId={props.currentGroup?._id}
            mode={SEARCH_MODE.OnlyEvents}
            textfieldPlaceholder={
              (calendar.groupServerID.length > 0
                ? t('searchItemsIn', {
                    what: t('events').toLowerCase(),
                    where: props.currentGroup?.Name ?? ''
                  })
                : is24meApp()
                  ? t('searchItems', {
                      what: t('eventsTasksNotes').toLowerCase()
                    })
                  : t('searchItems', { what: t('events').toLowerCase() })) ?? ''
            }
          />
        )}
      </Styled.CalendarHeaderActions>
    </Styled.CalendarHeader>
  )
}

function NavButtons(props: CalendarHeaderProps) {
  const calendar = useCalendar()

  return (
    <Box display={'flex'} flexDirection={'row'}>
      {separator()}
      <Styled.CalendarHeaderActionsCell>
        <Styled.RangeSelect
          sx={{ direction: 'ltr' }}
          variant="no-border"
          id="calendar-view-select"
          placeholder="Range"
          className={`select-view ${calendar.view}`}
          size="lg"
          options={RANGE}
          value={calendar.view}
          onChange={(e) => {
            console.log(calendar.ref)
            calendar.onView(e?.value as CalendarRange)
          }}
        />
        {separator()}
      </Styled.CalendarHeaderActionsCell>
      <GroupcalTooltip title={t('backToToday')}>
        <Grid2
          id="go-today-button"
          className="today-button"
          onClick={calendar.onGoToToday}
          sx={{
            marginLeft:
              i18n.dir() === 'ltr'
                ? isMobileDevice()
                  ? '6px'
                  : '8px'
                : '16px',
            marginRight:
              i18n.dir() === 'rtl'
                ? isMobileDevice()
                  ? '6px'
                  : '8px'
                : isMobileDevice() || isGuest()
                  ? undefined
                  : '16px',
            [theme.breakpoints.down('lg')]: {
              marginRight: 0,
              marginLeft: 0
            },
            '@media (max-height: 480px)': {
              marginRight: 0,
              marginLeft: 0
            }
          }}
        >
          <span className="today-text">{t('today')}</span>
          <CalendarBackIcon className="today-image" />
        </Grid2>
      </GroupcalTooltip>
    </Box>
  )
}

function GroupTitleElement(props: CalendarHeaderProps) {
  const calendar = useCalendar()

  const [groupPoupOpen, setGroupPopupOpen] = useState(false)
  const isMdUp = useMediaQuery(theme.breakpoints.up('lg'))
  const isHeightBelowCondesed = useMediaQuery('(max-height: 480px)')

  useEffect(() => {
    if (!groupPoupOpen) {
      calendar.onGroupInfo(undefined)
    }
  }, [groupPoupOpen])

  useEffect(() => {
    console.log('CalendarInfo', calendar.groupInfo)
    if (calendar.groupInfo) showGroup()
    else hideGroup()
  }, [calendar.groupInfo])

  const isUserNotActive =
    props.currentGroup &&
    !isUserActiveInGroup(
      props.currentGroup,
      LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
    )

  const showGroup = () => {
    if (isGuest()) return
    setGroupPopupOpen(true)
  }

  const hideGroup = () => {
    setGroupPopupOpen(false)
  }

  const readOnly = props.embedMode || isGuest()

  return (
    <Styled.CalendarHeaderTitleContainer className="calendar-info-wrapper">
      <GroupcalTooltip
        hidden={isGuest() || groupPoupOpen}
        title={t('clickToSeeCalendarInfo')}
      >
        <Styled.CalendarHeaderTitle>
          {props.title ? (
            <Typography
              fontSize={'1.5rem'}
              sx={{
                '@media (max-height: 480px)': {
                  fontSize: '16px'
                }
              }}
            >
              {props.title}
            </Typography>
          ) : (
            <GroupPopup
              setOpen={setGroupPopupOpen}
              isOpen={groupPoupOpen}
              groupID={props.currentGroup?._id}
              group={props.currentGroup}
            >
              <GroupTitle group={props.currentGroup} iconHeight={'20px'} />
            </GroupPopup>
          )}
        </Styled.CalendarHeaderTitle>
      </GroupcalTooltip>
      {readOnly || isGuest() || isHeightBelowCondesed ? (
        <></>
      ) : (
        <GroupcalTooltip
          hidden={isGuest() || groupPoupOpen}
          title={t('clickToSeeCalendarInfo')}
        >
          <Styled.CalendarHeaderSubtitle
            color={isUserNotActive ? 'red' : undefined}
          >
            {isUserNotActive && !isGuest() ? (
              isUserLeft(
                props.currentGroup,
                LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
              ) ? (
                'You left the shared calendar'
              ) : (
                'You have been removed from the shared calendar'
              )
            ) : readOnly ? (
              <></>
            ) : (
              props.currentGroup && (
                <NamesStrip
                  group={props.currentGroup}
                  participantIds={Object.keys(
                    getActiveParticipants(
                      props.currentGroup?.Participants ?? {}
                    )
                  )}
                />
              )
            )}
          </Styled.CalendarHeaderSubtitle>
        </GroupcalTooltip>
      )}
    </Styled.CalendarHeaderTitleContainer>
  )
}

function separator() {
  return (
    <Box
      width={'1px'}
      height={'30px'}
      margin={2}
      sx={{ backgroundColor: theme.palette.border.primary }}
    />
  )
}

function GroupPhotoElement(props: CalendarHeaderProps) {
  return (
    <ContainerWithBottomBadgeIcon
      showLock={
        !isGuest() &&
        isGroupOutOfTier(
          highestActiveTier(props.account),
          props.currentGroup,
          props.account
        )
      }
      topRightIcon={
        isGroupChannel(props.currentGroup) ? (
          <GroupChannelIcon width={18} height={18} />
        ) : undefined
      }
      marginRight={6}
    >
      <Avatar
        name=""
        asGroup
        img={props.currentGroup?.Photo}
        emptyPic={defaultGroupImage(props.currentGroup)}
        sx={{
          width: '40px',
          height: '40px',
          '@media (max-height: 480px)': {
            width: '26px !important',
            height: '26px !important'
          }
        }}
      />
    </ContainerWithBottomBadgeIcon>
  )
}

interface NextPrevButtonsProps {
  dateRangeSelected?: (selected: Date | DateRange | undefined) => void
}

function NextPrevButtons(props: NextPrevButtonsProps) {
  const isMdUp = useMediaQuery(theme.breakpoints.up('lg'))
  const calendar = useCalendar()

  const currentView = calendar.view

  const visibleAreaStart = dayjs(calendar.fromISO)
  const visibleAreaEnd = dayjs(calendar.toISO).subtract(1, 'day')

  const center = visibleAreaStart.add(2, 'week')

  const startMonth = visibleAreaStart.format('MMM')
  const startDay = visibleAreaStart.format('DD')
  const startYear = visibleAreaStart.format('YYYY')

  const endMonth = visibleAreaEnd.format('MMM')
  const endDay = visibleAreaEnd.format('DD')
  const endYear = visibleAreaEnd.format('YYYY')

  let dateRangeString = ''

  if (startMonth === endMonth && startYear === endYear) {
    // Same month and same year
    dateRangeString = `${startMonth} ${startDay} - ${endDay}, ${endYear}`
  } else if (startYear === endYear) {
    // Different months but same year
    dateRangeString = `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${endYear}`
  } else {
    // Different months and different years
    dateRangeString = `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${endYear}`
  }

  switch (currentView) {
    case CalendarRange.LISTDAY:
    case CalendarRange.DAY:
      dateRangeString = visibleAreaStart.format('MMM DD, YYYY')
      break
    case CalendarRange.MONTH:
    case CalendarRange.LISTWEEK:
      dateRangeString = center.format('MMM YYYY')
      break
  }

  if (isMobileDevice()) {
    dateRangeString =
      currentView === CalendarRange.MONTH
        ? center.format('MMM YYYY')
        : visibleAreaStart.format('MMM YYYY')
  }

  return (
    <Grid2
      display="flex"
      flexDirection={'row'}
      alignItems={'center'}
      whiteSpace={'nowrap'}
    >
      <Styled.IconButtonSlide
        data-prev={i18n.dir() === 'ltr' ? 1 : undefined}
        data-next={i18n.dir() === 'rtl' ? 1 : undefined}
        size="sm"
        onClick={calendar.prev}
      >
        <CaretDownIcon />
      </Styled.IconButtonSlide>
      <Styled.IconButtonSlide
        data-next={i18n.dir() === 'ltr' ? 1 : undefined}
        data-prev={i18n.dir() === 'rtl' ? 1 : undefined}
        size="sm"
        onClick={calendar.next}
      >
        <CaretDownIcon />
      </Styled.IconButtonSlide>
      <Box
        display={'flex'}
        alignItems={'center'}
        justifyContent={'center'}
        alignContent={'center'}
        flexDirection={'row'}
        sx={{
          flexGrow: 1,
          textAlign: 'center',
          fontSize: '1rem',
          height: 52,
          borderRadius: 23,
          cursor: 'pointer',
          minWidth: isMobileDevice()
            ? '100px'
            : calendar.view === CalendarRange.MONTH ||
                calendar.view === CalendarRange.LISTWEEK ||
                calendar.view === CalendarRange.DAY ||
                calendar.view === CalendarRange.LISTDAY
              ? '175px'
              : '250px',
          color: theme.palette.text.primary,
          '@media (min-height: 481px)': {
            fontSize: '1.25rem !important'
          },
          '@media (max-height: 480px)': {
            height: 32,
            minWidth:
              calendar.view === CalendarRange.MONTH ||
              calendar.view === CalendarRange.LISTWEEK ||
              calendar.view === CalendarRange.DAY ||
              calendar.view === CalendarRange.LISTDAY
                ? '150px'
                : '200px'
          },
          '&:hover': {
            // border: `1px solid ${theme.palette.input.border.primary}`,
            boxShadow: '0px 1px 6px 0px #32324714 inset',
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <ViewDatesPicker
          mode={currentView === CalendarRange.WEEK ? 'range' : 'single'}
          defaultRange={{
            from: visibleAreaStart.toDate(),
            to: visibleAreaEnd.toDate()
          }}
          defaultDate={
            calendar.view === CalendarRange.MONTH
              ? center.toDate()
              : visibleAreaStart.toDate()
          }
          onSelect={props.dateRangeSelected}
          withoutSelection={calendar.view === CalendarRange.MONTH}
        >
          <Grid2 display={'flex'} flexDirection={'row'} alignItems={'center'}>
            <div style={{ paddingLeft: 8, paddingRight: 8 }}>
              {dateRangeString}
            </div>
            <CaretDownIcon
              style={{
                marginRight: i18n.dir() === 'ltr' ? 8 : 0,
                marginLeft: i18n.dir() === 'rtl' ? 8 : 0
              }}
            />
          </Grid2>
        </ViewDatesPicker>
      </Box>
    </Grid2>
  )
}

export default withObservables(['groupID'], (props: CalendarHeaderProps) => ({
  account: ObserveService.Account(),
  groups: ObserveService.Groups(),
  userSettings: ObserveService.UserSettings(),
  currentGroup: ObserveService.GroupByServerID(props.groupID)
}))(CalendarHeader)
